import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { supabase } from "@/services/supabase";
import { Button } from "@/components/ui/button";
import { Download, ExternalLink, Printer } from "lucide-react";
import { Helmet } from "react-helmet-async";
import "../../styles/certificate.css";
import html2canvas from "html2canvas";

// Lấy BASE_URL từ environment hoặc sử dụng giá trị mặc định
const BASE_URL = import.meta.env.VITE_API_URL || "https://giacngo.org";

/**
 * Trang hiển thị certificate riêng biệt, không có header và footer
 * URL: /certificate/CERT-ID
 */
const Certificate: React.FC = () => {
  const { certificateId } = useParams<{ certificateId: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [certificate, setCertificate] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const certificateRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    async function fetchCertificate() {
      if (!certificateId) {
        setError("Mã chứng chỉ không hợp lệ");
        setLoading(false);
        return;
      }

      try {
        // Gọi API để lấy thông tin certificate
        const { data, error } = await supabase
          .from("certificates")
          .select(
            `
            *,
            courses (
              name,
              description
            )
          `,
          )
          .eq("certificate_id", certificateId)
          .single();

        if (error) {
          throw error;
        }

        if (!data) {
          throw new Error("Không tìm thấy chứng chỉ");
        }

        setCertificate(data);
      } catch (error: any) {
        console.error("Error fetching certificate:", error);
        setError(error.message || "Đã xảy ra lỗi khi tải chứng chỉ");
      } finally {
        setLoading(false);
      }
    }

    fetchCertificate();
  }, [certificateId]);

  const handlePrintCertificate = async () => {
    if (!certificateRef.current) return;

    try {
      // Hiển thị thông báo đang chuẩn bị in
      const loadingEl = document.createElement("div");
      loadingEl.className =
        "fixed top-0 left-0 w-full h-full bg-white bg-opacity-80 flex items-center justify-center z-50";
      loadingEl.innerHTML = `
        <div class="text-center">
          <div class="spinner-border animate-spin inline-block w-8 h-8 border-4 rounded-full border-t-transparent border-blue-500"></div>
          <p class="mt-4 text-gray-600">Đang chuẩn bị in...</p>
        </div>
      `;
      document.body.appendChild(loadingEl);

      // Lấy canvas của phần certificate
      const canvas = await html2canvas(certificateRef.current, {
        scale: 2, // Tăng scale để có chất lượng tốt hơn
        useCORS: true, // Cho phép tải hình ảnh từ domain khác
        logging: false,
        backgroundColor: "#fff8e6", // Màu nền của certificate
      });

      // Xóa thông báo đang tải
      document.body.removeChild(loadingEl);

      // Tạo iframe mới để in
      const printFrame = document.createElement("iframe");
      printFrame.style.position = "fixed";
      printFrame.style.right = "0";
      printFrame.style.bottom = "0";
      printFrame.style.width = "0";
      printFrame.style.height = "0";
      printFrame.style.border = "0";
      document.body.appendChild(printFrame);

      // Tạo nội dung cho iframe
      const printDocument = printFrame.contentWindow?.document;
      if (!printDocument) return;

      // Viết HTML cho iframe với canvas đã tạo
      printDocument.open();
      printDocument.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>Chứng nhận Giác Ngộ - ${certificate?.recipient_name}</title>
          <style>
            body {
              margin: 0;
              padding: 0;
              display: flex;
              justify-content: center;
              align-items: center;
              min-height: 100vh;
            }
            img {
              max-width: 100%;
              height: auto;
              display: block;
              margin: 0 auto;
              box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
            }
            @media print {
              body {
                margin: 0;
                padding: 0;
              }
              img {
                max-width: 100%;
                max-height: 100vh;
                box-shadow: none;
              }
            }
          </style>
        </head>
        <body>
          <img src="${canvas.toDataURL("image/png")}" alt="Certificate" />
        </body>
        </html>
      `);
      printDocument.close();

      // Chờ iframe load xong và in
      printFrame.onload = () => {
        // Timeout để đảm bảo hình ảnh đã load
        setTimeout(() => {
          printFrame.contentWindow?.focus();
          printFrame.contentWindow?.print();

          // Xóa iframe sau khi in hoặc hủy
          setTimeout(() => {
            document.body.removeChild(printFrame);
          }, 500);
        }, 500);
      };
    } catch (error) {
      console.error("Error printing certificate:", error);
      alert("Có lỗi xảy ra khi in chứng chỉ. Vui lòng thử lại sau.");
    }
  };

  const handleViewOnPolygonScan = () => {
    if (certificate?.blockchain_verification_url) {
      window.open(certificate.blockchain_verification_url, "_blank");
    } else if (certificate?.nft_transaction_hash) {
      window.open(
        `https://polygonscan.com/tx/${certificate.nft_transaction_hash}`,
        "_blank",
      );
    }
  };

  const handleDownloadCertificate = async () => {
    if (!certificateRef.current) return;

    try {
      // Hiển thị thông báo đang chuẩn bị tải
      const loadingEl = document.createElement("div");
      loadingEl.className =
        "fixed top-0 left-0 w-full h-full bg-white bg-opacity-80 flex items-center justify-center z-50";
      loadingEl.innerHTML = `
        <div class="text-center">
          <div class="spinner-border animate-spin inline-block w-8 h-8 border-4 rounded-full border-t-transparent border-blue-500"></div>
          <p class="mt-4 text-gray-600">Đang chuẩn bị tải...</p>
        </div>
      `;
      document.body.appendChild(loadingEl);

      // Lấy canvas của phần certificate
      const canvas = await html2canvas(certificateRef.current, {
        scale: 2, // Tăng scale để có chất lượng tốt hơn
        useCORS: true, // Cho phép tải hình ảnh từ domain khác
        logging: false,
        backgroundColor: "#fff8e6", // Màu nền của certificate
      });

      // Xóa thông báo đang tải
      document.body.removeChild(loadingEl);

      // Tạo đường dẫn tải xuống
      const image = canvas.toDataURL("image/png", 1.0);
      const downloadLink = document.createElement("a");
      downloadLink.href = image;
      downloadLink.download = `Certificate-${certificate?.recipient_name}-${certificate?.certificate_id}.png`;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
    } catch (error) {
      console.error("Error downloading certificate:", error);
      alert("Có lỗi xảy ra khi tải chứng chỉ. Vui lòng thử lại sau.");
    }
  };

  // Hiển thị trạng thái loading
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <span className="visually-hidden">{t("loading", { ns: "common" })}</span>
          <p className="mt-4 text-gray-600">{t("loading", { ns: "common" })}</p>
        </div>
      </div>
    );
  }

  // Hiển thị thông báo lỗi
  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="max-w-lg w-full bg-white p-8 rounded-lg shadow-md">
          <h1 className="text-2xl font-bold text-red-600 mb-4">
            Không tìm thấy chứng chỉ
          </h1>
          <p className="text-gray-700 mb-6">{error}</p>
          <Button onClick={() => navigate("/verify-certificate")}>
            Quay lại trang xác minh chứng chỉ
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Chứng nhận Giác Ngộ - {certificate?.recipient_name}</title>
        <link rel="stylesheet" href="/certificate-responsive.css" />
      </Helmet>

      <div className="container mx-auto px-4 py-6 max-w-4xl print:py-0">
        {/* NFT Certificate Header */}
        <div className="mb-4 flex justify-between items-center print:hidden">
          <h1 className="text-xl font-semibold text-blue-700">
            NFT Certificate
          </h1>
          <div className="flex space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handlePrintCertificate}
              className="flex items-center gap-1"
            >
              <Printer className="h-4 w-4" />
              In chứng chỉ
            </Button>
            {certificate?.blockchain_verification_url && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleViewOnPolygonScan}
                className="flex items-center gap-1 text-green-700 border-green-700 hover:bg-green-50"
              >
                <ExternalLink className="h-4 w-4" />
                Xem NFT trên Polygon Scan
              </Button>
            )}
          </div>
        </div>

        {/* Certificate Display */}
        <div className="bg-white p-2 rounded-lg shadow-md mb-6 certificate-container">
          <div
            ref={certificateRef}
            className="certificate border-4 border-double border-amber-500 bg-amber-50 rounded-lg relative"
          >
            <div
              className="absolute inset-0 bg-center bg-contain bg-no-repeat opacity-5"
              style={{
                backgroundImage:
                  "url('https://imagedelivery.net/meCI2KDHmnUhVs_SxNm-Og/a34fe98e-40ce-43c4-d70b-036ae3ded900/public')",
              }}
            ></div>

            {/* Biểu tượng chìm ở 4 góc */}
            <div className="absolute top-4 left-4 h-16 opacity-20">
              <img
                src="https://imagedelivery.net/meCI2KDHmnUhVs_SxNm-Og/217a27a8-7f1e-4370-9d04-6a8be0bf3b00/public"
                alt="corner"
                className="w-full h-full"
                crossOrigin="anonymous"
              />
            </div>
            <div className="absolute top-4 right-4 h-16 opacity-20 transform">
              <img
                src="https://imagedelivery.net/meCI2KDHmnUhVs_SxNm-Og/217a27a8-7f1e-4370-9d04-6a8be0bf3b00/public"
                alt="corner"
                className="w-full h-full"
                crossOrigin="anonymous"
              />
            </div>
            <div className="absolute bottom-4 left-4 h-16 opacity-20 transform">
              <img
                src="https://imagedelivery.net/meCI2KDHmnUhVs_SxNm-Og/217a27a8-7f1e-4370-9d04-6a8be0bf3b00/public"
                alt="corner"
                className="w-full h-full"
                crossOrigin="anonymous"
              />
            </div>
            <div className="absolute bottom-4 right-4 h-16 opacity-20 transform">
              <img
                src="https://imagedelivery.net/meCI2KDHmnUhVs_SxNm-Og/217a27a8-7f1e-4370-9d04-6a8be0bf3b00/public"
                alt="corner"
                className="w-full h-full"
                crossOrigin="anonymous"
              />
            </div>

            <div className="p-6 md:p-10">
              {/* Thông tin xác thực ở phía trên */}
              <div className="absolute top-2 right-4 text-right">
                <p className="certificate-issue-date">
                  Ngày cấp:{" "}
                  {new Date(certificate?.issue_date).toLocaleDateString(
                    "vi-VN",
                  )}
                </p>
                <p className="certificate-id">
                  Mã chứng chỉ: {certificate?.certificate_id}
                </p>
              </div>

              {/* Logo và tiêu đề */}
              <div className="text-center mb-6 mt-4">
                <div className="flex justify-center mb-2">
                  <img
                    src="https://giacngo.org/logo.png"
                    alt="Giác Ngộ"
                    className="h-16"
                    crossOrigin="anonymous"
                  />
                </div>
                <h1 className="font-bold text-amber-800 mt-2">
                  CHỨNG NHẬN GIÁC NGỘ
                </h1>
                <div className="certificate-border-line"></div>
              </div>

              {/* Thông tin người nhận */}
              <div className="text-center mb-6">
                <p className="mb-2">Chứng nhận này được trao cho</p>
                <h2 className="font-bold mb-4 text-amber-900">
                  {certificate?.recipient_name}
                </h2>
                <p className="mb-2">đã hoàn thành xuất sắc khóa học</p>
                <h3 className="font-bold text-amber-800 px-4">
                  {certificate?.courses?.name ||
                    "Kinh Kim Cang: Tinh Hoa Trí Tuệ Phật Giáo"}
                </h3>
              </div>

              {/* QR code và chữ ký */}
              <div className="grid grid-cols-2 gap-4 mt-8">
                <div className="qr-code text-center">
                  <img
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=https://giacngo.org/verify-certificate/${certificate?.certificate_id}`}
                    alt="QR Code"
                    className="w-20 h-20 mx-auto"
                    crossOrigin="anonymous"
                  />
                  <p className="text-xs mt-1">Quét để xác thực</p>
                </div>

                <div className="signature text-center">
                  <img
                    src="https://imagedelivery.net/meCI2KDHmnUhVs_SxNm-Og/a2fbcefe-5221-43b4-fe58-fcc89425fc00/public"
                    alt="Signature"
                    className="h-12 mx-auto mb-2"
                    crossOrigin="anonymous"
                  />
                  <p className="font-semibold">Giác Ngộ</p>
                  <p className="text-sm">giacngo.org</p>
                </div>
              </div>
            </div>

            {/* Blockchain Network ở dưới cùng như logo chìm */}
            <div className="absolute bottom-2 left-0 right-0 flex justify-center">
              <a
                href={
                  certificate?.nft_transaction_hash
                    ? `https://polygonscan.com/tx/${certificate.nft_transaction_hash}`
                    : certificate?.blockchain_verification_url || "#"
                }
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center cursor-pointer hover:text-blue-600 transition-colors opacity-50 hover:opacity-100"
                title="Xem giao dịch trên Polygon blockchain"
                onClick={(e) => {
                  if (
                    !certificate?.nft_transaction_hash &&
                    !certificate?.blockchain_verification_url
                  ) {
                    e.preventDefault();
                  }
                }}
              >
                <img
                  src="https://imagedelivery.net/meCI2KDHmnUhVs_SxNm-Og/0de7063b-6f02-4abb-eabc-22b96cb8c000/public"
                  alt="Polygon"
                  className="h-4 mr-1"
                  crossOrigin="anonymous"
                />
                <span className="text-xs">Blockchain Network: Polygon</span>
              </a>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col md:flex-row justify-center gap-3 print:hidden">
          <Button
            className="flex items-center gap-1"
            variant="default"
            onClick={handleDownloadCertificate}
          >
            <Download className="h-4 w-4" />
            Tải chứng chỉ
          </Button>

          <Button
            variant="outline"
            onClick={() =>
              navigate(`/verify-certificate/${certificate?.certificate_id}`)
            }
          >
            Xác minh chứng chỉ này
          </Button>

          {certificate?.nft_transaction_hash && (
            <Button
              variant="outline"
              className="flex items-center gap-1 text-purple-700 border-purple-700 hover:bg-purple-50"
              onClick={() =>
                window.open(
                  `https://polygonscan.com/tx/${certificate.nft_transaction_hash}`,
                  "_blank",
                )
              }
            >
              <img
                src="https://imagedelivery.net/meCI2KDHmnUhVs_SxNm-Og/0de7063b-6f02-4abb-eabc-22b96cb8c000/public"
                alt="Polygon"
                className="h-5 mr-1"
                crossOrigin="anonymous"
              />
              Transaction
            </Button>
          )}

          {certificate?.blockchain_verification_url && (
            <Button
              variant="outline"
              className="flex items-center gap-1 text-green-700 border-green-700 hover:bg-green-50"
              onClick={handleViewOnPolygonScan}
            >
              <ExternalLink className="h-4 w-4" />
              Xem NFT trên Polygon Scan
            </Button>
          )}
        </div>
      </div>
    </>
  );
};

export default Certificate;
